import './index.css';

import { reloadOnStaleChunk } from '@akpl/shared';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { fetchPublicConfig } from './api';
import { App } from './App';
import { createFirebaseAuthClient } from './auth/client';
import { BootSkeleton } from './components/ui/skeleton';
import { initCmsI18n } from './i18n';

/**
 * Boot: i18n ‖ `/v1/config` → Firebase Auth → app. Nothing is baked into the bundle, so the
 * same artifact runs against the local stack and prod (docs/09 §1).
 *
 * QA 2026-09-15 **UX-504**: the boot used to be strictly serial — `await initCmsI18n()`, then
 * `await fetchPublicConfig()`, then Firebase — behind a blank page and then a bare "Cargando…".
 * The two independent steps now run together (the translation bundle is local, the config is a
 * round trip to a worker that may be cold), and the shell skeleton is painted before either of
 * them is awaited, so the first frame is the console taking shape rather than an empty canvas.
 */
// A console left open across a deploy asks for files that no longer exist: reload instead of dying.
reloadOnStaleChunk();

async function boot() {
  const root = createRoot(document.getElementById('root')!);
  root.render(<BootSkeleton />);

  let config;
  try {
    [, config] = await Promise.all([initCmsI18n(), fetchPublicConfig()]);
  } catch (error) {
    root.render(
      <main style={{ padding: 24, fontFamily: 'Inter, system-ui', color: '#cf1a20' }}>
        Cannot reach the API: {error instanceof Error ? error.message : String(error)}
      </main>,
    );
    return;
  }

  const authClient = await createFirebaseAuthClient(config.firebase);
  root.render(
    <StrictMode>
      <App authClient={authClient} config={config} />
    </StrictMode>,
  );
}

void boot();
